import { ChangeDetectorRef, Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Apollo } from 'apollo-angular';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { TableLazyLoadEvent } from 'primeng/table';
import { NXDataSourcePrime } from '../../services/dataSource/NXDataSourcePrime';
import { ApiKey, ApiKeyCreateResult } from '../../entities/apiKey/apiKey';
import { ApiKeysService } from '../../services/apiKeys/apiKeys.service';
import { LoadingService } from '../../services/loading.service';
import { RolesService } from '../../services/roles/roles.service';
import { ToastService } from '../../services/toast/toast.service';
import { UsersService } from '../../services/users/users.service';
import { ApiKeyDialogComponent } from '../api-key-dialog/apikey-dialog.component';
import { ConfirmDialogEventType, ConfirmDialogSeverity } from '../confirmDialog/confirmDialogComponent';
import { ConfirmDialogService } from '../confirmDialog/confirmDialogService';
import { EditDialogService } from '../editDialog/editDialogService';
import { ApiKeyDataSource } from './apiKeyDataSource';

@Component({
  selector: 'app-api-keys',
  templateUrl: './api-keys.component.html',
  styleUrls: ['./api-keys.component.scss']
})
export class ApiKeysComponent implements OnInit, OnDestroy {

  currentTenantId: string | undefined;

  dialogRef: DynamicDialogRef | undefined;

  public sortProperty: string;
  public defaultSortProperty: string = 'id';
  public sortDirection: string;
  public defaultSortDirection: string = 'asc';
  public pageNumber = 0;
  public pageSize = 15;
  public sortModel: any;
  public sortOptions = [
    { label: 'Key-ID aufsteigend', fieldName: 'id', direction: "asc"},
    { label: 'Key-ID absteigend', fieldName: 'id', direction: "desc" }
  ];

  public dataSource: ApiKeyDataSource;
  public loading = false;

  public createApiKeyResult: ApiKeyCreateResult;
  // for testing purposes:
  // = new ApiKeyCreateResult(
  //   {
  //     'apiKeyData': 'Njg2YjBiMTcuMjZhNDc4OWUtMGZlYy00NmNlLWIyNTUtYTllNWI2MmQ2MDY0',
  //     'apiKeyId': '33c4420e-9a19-4031-996f-b84466c4b102'
  //   }
  // );

  constructor(
    private router: Router,
    private apollo: Apollo,
    private loadingService: LoadingService,
    private apiKeysService: ApiKeysService,
    private cdRef: ChangeDetectorRef,
    @Inject('env') public environment : any,
    public rolesService: RolesService,
    private confirmDialogService: ConfirmDialogService,
    public dialogService: EditDialogService,
    public toastService: ToastService,
    public userService: UsersService
  ) {
    this.setTableDataSource();
  }

  ngOnInit(): void {
    this.currentTenantId = this.userService.getCurrentTenantId();
  }

  setTableDataSource(): void{
    this.dataSource = new ApiKeyDataSource(this.apollo, this.environment);
    this.dataSource.loading.subscribe((loading) => {
      this.loading = loading;
    });
    this.dataSource.loadApiKeys({
      pageNumber: 0,
      pageSize: 15,
      sortProperty: this.sortProperty ? this.sortProperty : this.defaultSortProperty,
      sortDirection: this.sortDirection ? this.sortDirection : this.defaultSortDirection
    });
  }

  refetchApiKeys(force = false): void {
    this.dataSource.refetchQuery({
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      sortProperty: this.sortProperty ? this.sortProperty : this.defaultSortProperty,
      sortDirection: this.sortDirection ? this.sortDirection : this.defaultSortDirection,
    }, force);
  }

  ngOnDestroy(): void {
    this.dataSource.disconnect();
  }

  loadData(event: TableLazyLoadEvent) {
    this.loading = true;

    // sorting
    if (event?.sortField !== undefined && typeof event?.sortField == 'string') {
      this.sortProperty = event.sortField;
    }
    if (event?.sortOrder !== undefined) {
      this.sortDirection = NXDataSourcePrime.translateSortOrderToSortDirection(event.sortOrder);
    }

    //filtering
    // no filtering yet

    // pagination
    this.pageNumber = event?.first !== undefined ? event.first / event.rows : 0;
    this.pageSize = event?.rows !== undefined ? event.rows : 15;

    this.refetchApiKeys();
  }

  sort(event: any) {
    if(event?.value?.direction && event?.value?.fieldName){
      this.sortProperty = event.value.fieldName;
      this.sortDirection = event.value.direction;
    } else {
      this.sortProperty = this.defaultSortProperty;
      this.sortDirection = this.defaultSortDirection;
    }
    this.cdRef.detectChanges();
  }

  openDocumentation(): void {
    window.open(this.environment.documentationUri, '_blank');
  }

  copyToClipboard(stringToCopy: string): void {
    navigator.clipboard.writeText(stringToCopy).then(() => {
      // copied
    }).catch(err => {
      // error
    });
  }

  deleteApiKey(apiKey: ApiKey) {
    const message = 'Möchtest Du den Api-Key ' + apiKey.id + ' wirklich löschen?';
    this.dialogRef = this.confirmDialogService.open({
      message: message,
      severity: ConfirmDialogSeverity.WARN,
      header: 'Api-Key löschen'
    });
    this.dialogRef.onClose.subscribe( (result : ConfirmDialogEventType) => {
      if(result === ConfirmDialogEventType.CONFIRM){
        this.loadingService.setLoading(true);
        this.apiKeysService.deleteApiKey(apiKey.id, () => {
          this.refetchApiKeys(true);
          this.loadingService.setLoading(false);
          this.toastService.addToastWithMessage('Api-Key gelöscht.');
        });
      };
    })
      // do nothing
  };

  createApiKey(): void {
    this.dialogRef = this.dialogService.open(ApiKeyDialogComponent, {
      header: 'Api-Key erstellen'
    });
    this.dialogRef.onClose.subscribe((result) => {
      // result is true or false, indiciating if it should refetch
      if(result){
        this.refetchApiKeys(true);
        this.loadingService.setLoading(false);
        this.createApiKeyResult = new ApiKeyCreateResult(result?.createApiKey);
        this.toastService.addToastWithMessage('Api-Key erstellt.');
      }
    });
  }

  closeCreatedApiKey(): void {
    this.createApiKeyResult = undefined;
  }

}
